/** Result of validating/clamping a store-credit "withdraw" amount entry -
 * `amount` is what should actually be applied (already clamped), `error` is
 * the message to show the cashier, or `""` if the entry is valid as-is. */
export interface IWithdrawAmountResult {
  amount: number;
  error: string;
}

/** Pure validation/clamping for a `StoreCreditSection` WITHDRAW entry -
 * kept dependency-free (no React/redux imports) so it can be unit-tested
 * directly without pulling in the app's full redux/axios/router module
 * graph via `StoreCreditSection`'s own imports.
 *
 * Unlike CASH (real physical change) or CARD/CHECK (blocked entirely unless
 * explicitly routed to store credit via `payment.tsx`'s "Save balance to
 * store credits" toggle), there is no legitimate reason to ever withdraw
 * MORE store credit than the bill's own due (`billValue`) - the "excess"
 * wouldn't become change or get routed anywhere, it would just be silently
 * dropped by `PickupModal.tsx`/`payment-modal.tsx`'s own `makePayment`
 * due-amount cap, while `StoreCreditSection`'s "New Balance" preview (which
 * uses the entered amount as-is) would misleadingly suggest the customer's
 * full entered amount was deducted. Cap entry at the due amount here, same
 * as the pre-existing `currentBalance` cap, so the preview and the amount
 * that actually gets sent always match. See PV-TASK-49's investigation/PR
 * for the full reasoning (this was a real, previously-unguarded gap - every
 * other payment method already has an equivalent guard rail of its own). */
export function clampWithdrawAmount(
  value: number,
  currentBalance: number,
  billValue: number
): IWithdrawAmountResult {
  const dueAmount = billValue ?? 0;
  if (value > currentBalance) {
    return {
      amount: currentBalance,
      error: `Amount cannot exceed available balance of $${currentBalance.toFixed(2)}`,
    };
  }
  if (value > dueAmount) {
    return {
      amount: dueAmount,
      error: `Amount cannot exceed the amount due of $${dueAmount.toFixed(2)}`,
    };
  }
  if (value < 0) {
    return { amount: 0, error: "Amount cannot be negative" };
  }
  return { amount: value, error: "" };
}
